import { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { apiFetch } from '../../api/client';
import type { GoalTaskType, Paginated, TaskTerm } from '../../api/types';

/** Wait this long after the last keystroke before asking for suggestions. */
const DEBOUNCE_MS = 250;
/** Suggestions start from this many typed characters. */
const MIN_CHARS = 2;
const MAX_SUGGESTIONS = 8;

interface Props {
  /** Parts are suggested for "buy", jobs for "do". */
  type: GoalTaskType;
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
}

/**
 * A task's name field that suggests known parts or jobs (in the app's
 * language) as you type. Anything can still be typed — suggestions only help.
 */
export function TaskTermAutocomplete({
  type,
  value,
  onChange,
  label,
  placeholder,
  required,
  autoFocus,
}: Props) {
  const [options, setOptions] = useState<TaskTerm[]>([]);

  useEffect(() => {
    const search = value.trim();
    if (search.length < MIN_CHARS) {
      setOptions([]);
      return;
    }

    let current = true;
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams({
        'filter[type]': type,
        'filter[search]': search,
        per_page: String(MAX_SUGGESTIONS),
      });
      apiFetch<Paginated<TaskTerm>>(`/api/v1/task-terms?${query}`)
        .then((res) => current && setOptions(res.data))
        .catch(() => current && setOptions([]));
    }, DEBOUNCE_MS);

    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [type, value]);

  return (
    <Autocomplete
      freeSolo
      options={options}
      // The server already filtered and ranked them.
      filterOptions={(all) => all}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      inputValue={value}
      onInputChange={(_, next) => onChange(next)}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <Box component="li" key={key} {...rest}>
            <Box>
              <Typography variant="body2">{option.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.category}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          autoFocus={autoFocus}
          fullWidth
        />
      )}
    />
  );
}
